import { Cancel, Star } from '@mui/icons-material';
import { Autocomplete, TextField } from '@mui/material';
import Loading from 'components/Loading';
import Tag from "components/Tag";
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import get from 'lodash/get';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useCurrentClass } from "store/classes/selectors";
import { handleListClassSubmissions, handleReturnSubmission } from "store/submissions/thunks";
import { useCurrentUser } from "store/users/selectors"; // Update path if needed
import styled from "styled-components";
import COLOR from "utils/constants/color";
import { useCollectionNotification } from "utils/methods/firebaseNotification";
import { v4 } from 'uuid';
import SentryWrap from 'components/Sentry';

const Note = styled.span`
  color: ${COLOR.black400};
  display: block;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
`;

const CloseIcon = ({ onClose }) => (
  <div onClick={onClose} className="cancel-icon-station">
    <Cancel />
  </div>
);

const ReturnAssignmentByStudent = ({ open, close, submissionList = [] }) => {
  const showAlert = useAlert();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const currentClass = useCurrentClass();
  const currentUser = useCurrentUser();
  const { createCollectionNotification } = useCollectionNotification();

  const disabled = useMemo(() => submissionList.length === 0, [submissionList]);

  const studentOptions = useMemo(() => (
    submissionList.map(sub => ({
      label: `${sub.firstName} ${sub.lastName}`.trim(),
      value: sub.submission_id
    }))
  ), [submissionList]);

  const selectedSubmissions = useMemo(() => (
    submissionList.filter(sub => selectedIds.includes(sub.submission_id))
  ), [selectedIds, submissionList]);

  useEffect(() => {
    if (open) {
      document.body.classList.add("body-overflow-hidden");
    }
    return () => {
      document.body.classList.remove("body-overflow-hidden");
    };
  }, [open]);

  const returnAssignment = async (submission) => {
    if (submission?.submission?.id) {
      const res = await dispatch(handleReturnSubmission(submission.submission.id));
      if (res?.id) {
        returnNotification(submission.submission);
      }else{
        showAlert(`ERR-GR001 Error in returning`,"error")
      } 
    } 
  };

  const returnNotification = (submission) => {
    const notificationData = {
      id: v4(),
      classId: get(currentClass, 'id', ''),
      displayName: get(submission, 'assignment.displayName', ''),
      senderId: get(currentUser, 'id', ''),
      receiversId: [get(submission, 'userId', '')],
      modified: moment().toISOString(),
      data: {
        chapter: get(submission, 'assignment.chapter', ''),
        page: get(submission, 'assignment.page', ''),
        id: get(submission, 'assignment.id', ''),
      },
      message: 'Your Teacher Returned this Assignment',
      type: 'update_quiz',
      userSeen: []
    };
    sendNotifications(notificationData);
  };

  const sendNotifications = async (data) => {
    try {
      await createCollectionNotification(data, get(currentClass, 'id', ''));
    } catch (err) {
      console.error("Notification error:", err);
    }
  };

  const handleDone = async () => {
    if (selectedSubmissions.length === 0) {
      showAlert("No students selected.", "warning");
      return;
    }

    showAlert("Returning assignments...","info");
    setLoading(true);
    try {
      for (let submission of selectedSubmissions) {
        await returnAssignment(submission);
      }
      await dispatch(handleListClassSubmissions(currentClass.id))
      showAlert("Assignments returned successfully.", "success");
    } catch (err) {
      console.error(err);
      showAlert("An error occurred while returning assignments.", "error");
    } finally {
      setLoading(false);
      close();
    }
  };

  if (!open) return null;

  return (
    <SentryWrap>
      <div className="custom-fullscreen-container">
        <div className="deactivate-student-container">
        <div className="deactivate-student-inner-container">
          <CloseIcon onClose={close} />

          <div className="flex" style={{ flexDirection: "column" }}>
            <Autocomplete
              multiple
              options={studentOptions}
              getOptionLabel={(option) => option.label}
              value={studentOptions.filter(opt => selectedIds.includes(opt.value))}
              onChange={(event, value) => setSelectedIds(value.map(v => v.value))}
              style={{ width: "100%" }}
              disabled={disabled}
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select Students for return"
                  placeholder="Select Students"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />

            <div className="flex items-center mt-10px">
              <Tag height={20}>
                <Star style={{ height: "12px", margin: "-1px 4px 0px 0px", width: "12px" }} />
                NOTE
              </Tag>
              <Note>
                The assignment will be returned for the selected students.
              </Note>
            </div>
          </div>

          <div className="flex items-center w-full justify-end cancel-done-container">
            <button onClick={close} className="cancel-button">Cancel</button>
            <button
              disabled={loading}
              style={{ opacity: loading ? 0.5 : 1 }}
              onClick={handleDone}
              className="done-button"
            >
              {loading ? <Loading size={20} white /> : 'Done'}
            </button>
          </div>
        </div>
      </div>
      </div>
    </SentryWrap>
  );
};

export default ReturnAssignmentByStudent;